/**
 * The message catalogue.
 *
 * English is the source of truth: `Messages` is derived from it, so adding a
 * key here makes every other locale fail to typecheck until it is either
 * translated or deliberately left to fall back. Other locales are `Partial`,
 * and anything missing falls through to English rather than showing a key.
 *
 * The languages covered are the ones Escape from Tarkov ships an interface in,
 * so a player who has the game in Polish can have this in Polish too.
 */

export const en = {
  /* Connection state ------------------------------------------------- */
  "status.syncing": "Synced",
  "status.connecting": "Connecting",
  "status.full": "Full",
  "status.closed": "Offline",
  "toast.connecting": "Connecting…",
  "toast.reconnecting": "Disconnected. Reconnecting…",
  "toast.retrying": "Retrying shortly.",
  "toast.busy": "Busy right now",
  "notice.mapChanged": "{name} switched the map",

  /* Placing pins ------------------------------------------------------ */
  "hint.place": "Click to place, drag to aim (Esc to cancel)",

  /* Pin kinds --------------------------------------------------------- */
  "kind.objective": "Objective",
  "kind.rally": "Rally",
  "kind.enemy": "Enemy",
  "kind.loot": "Loot",
  "kind.extract": "Extract",
  "kind.danger": "Danger",
  "kind.self": "Me",
  "kind.ping": "Here!",
  "hint.objective": "Where you are heading",
  "hint.rally": "Where to regroup",
  "hint.enemy": "Point it the way you saw them",
  "hint.loot": "Rare containers, bodies",
  "hint.extract": "The extract you plan to use",
  "hint.danger": "Mines, snipers, open ground",
  "hint.self": "Your position. Placing it again removes the old one",
  "hint.ping": "A temporary marker that fades after 25 seconds",

  /* Toolbar ----------------------------------------------------------- */
  "tool.clearMine": "Clear mine",
  "tool.clearMineHint": "Removes every pin you placed",

  /* Sidebar ----------------------------------------------------------- */
  "sidebar.map": "Map (shared with everyone)",
  "sidebar.styleSvg": "Illustrated",
  "sidebar.styleTile": "Satellite",
  "sidebar.floors": "Floor (only changes your view)",
  "sidebar.groundFloor": "Ground",
  "sidebar.floorNote": "Pins on other floors are dimmed.",
  "sidebar.floorOtherStyle": "* switches the map to {style}.",
  "sidebar.members": "{count} here",
  "sidebar.roomLink": "Link to this map",
  "sidebar.roomLinkCopy": "Copy the link",
  "sidebar.roomLinkCopied": "Copied",
  "sidebar.roomLinkNote": "Anyone with this link signs in with Discord and lands on the same map.",
  "sidebar.hide": "Hide the panel",
  "sidebar.show": "Show the panel",

  /* The list of everything on the map ----------------------------------- */
  /* Roughly where people come into the map; see fetch-spawns.mjs. */
  "spawns.show": "Show where you spawn",
  "spawns.note": "Roughly where a PMC raid starts. Not exact -- the game spreads them over a wide area.",
  "spawns.unnamed": "Entry {n}",
  "tool.eraser": "Rub out",
  "hint.eraser": "Rub pins and lines off the map",
  "hint.erasing": "Press or drag over anything to take it off the map. Esc to stop.",
  "tool.penColor": "Line colour",
  "tool.penColorMine": "Line colour (yours)",

  "list.title": "Pins and lines",
  "list.empty": "Nothing on this map yet.",
  "list.show": "Show it on the map",
  "list.remove": "Remove",
  "sidebar.you": "you",
  "sidebar.guest": "guest",
  "sidebar.clearAll": "Clear everyone's pins",
  "sidebar.openWindow": "Open in a window (just me)",
  "sidebar.invite": "Invite someone",
  "sidebar.help": "How to use",
  "sidebar.feedback": "Send feedback",
  "sidebar.mapData": "Map data",
  "sidebar.license": "Licence",
  "sidebar.licenseNote": "non-commercial use only",
  "sidebar.room": "Room",

  /* Extracts ----------------------------------------------------------- */
  "extracts.show": "Show extracts",
  "extracts.none": "No extract data for this map",
  // Upstream knows where it is but has no name for it yet.
  "extracts.unnamed": "Unnamed extract",
  "extracts.pmc": "PMC",
  "extracts.scav": "Scav",
  "extracts.shared": "Either",
  // Needs a PMC and a Scav in it together; the game calls it ScavCooperation.
  "extracts.coop": "Co-op",
  // Not an exit at all: it carries the raid on to another map.
  "extracts.transit": "Transit",

  /* Pin card ---------------------------------------------------------- */
  "pin.facing": "Facing",
  "pin.floor": "Floor",
  "pin.map": "Map",
  "pin.noFacing": "No direction",
  "pin.facingValue": "{compass} ({degrees}°)",
  "pin.notePlaceholder": "Add a note (e.g. third-floor window)",
  "pin.removeMine": "Delete this pin",
  "pin.removeOther": "Delete someone else's pin",
  "pin.close": "Close",
  "compass.n": "N",
  "compass.ne": "NE",
  "compass.e": "E",
  "compass.se": "SE",
  "compass.s": "S",
  "compass.sw": "SW",
  "compass.w": "W",
  "compass.nw": "NW",

  /* Handoff ----------------------------------------------------------- */
  "kind.draw": "Pen",
  "hint.draw": "Draw a line on the map",
  "hint.drawing": "Drag to draw; pick a colour on the right. Ctrl+Z takes back your last line, the rubber takes out any of them.",
  "handoff.title": "Open in a window",
  "handoff.lede":
    "A link for you. Opens the same room in a browser, so Discord can keep showing the stream while the map sits on another screen.",
  "handoff.open": "Open in this computer's browser",
  "handoff.or": "On another device",
  "handoff.codeHint": "On that device, go to {host}/j/ and enter the code above.",
  "handoff.copy": "Copy link",
  "handoff.copied": "Copied",
  "handoff.copyFailed": "Selected above — press Ctrl+C",
  "handoff.expiry":
    "Only you can open this link, and only for another {time}. " +
    "Whichever device opens it can come back to it later.",
  "handoff.warn":
    "Sent to someone else, this link will not open for them. " +
    "Use \"Invite someone\" to bring people in.",
  "handoff.creating": "Making a link…",
  "handoff.failed": "Could not make a link",

  /* Invite ------------------------------------------------------------ */
  "invite.title": "Invite someone",
  "invite.lede":
    "Bring in someone who is not on the voice call. They enter a name and can place pins.",
  "invite.copy": "Copy link",
  "invite.warn": "Anyone with this link can join. Do not post it publicly.",
  "invite.budget": "{left} more people, expires in {time} (used {used}/{max}).",
  "invite.revoke": "Revoke this link now",
  "invite.revoked": "Link revoked.",
  "invite.creating": "Making a link…",
  "invite.failed": "Could not make a link",
  "invite.revokeFailed": "Could not revoke it",

  /* Invite landing ----------------------------------------------------- */
  "join.title": "Join the map",
  "join.lede": "You have been invited to a raid map. Pick a name to show. Your pins will carry it.",
  "join.name": "Name",
  "join.go": "Join",
  "join.joining": "Joining…",
  "join.failed": "Could not join",

  /* Feedback ----------------------------------------------------------- */
  "feedback.title": "Send to the author",
  "feedback.request": "Request",
  "feedback.bug": "Bug",
  "feedback.other": "Other",
  "feedback.requestHint": "It would be nice if…",
  "feedback.bugHint": "Something is wrong",
  "feedback.otherHint": "Anything at all",
  "feedback.placeholder":
    "e.g. I would like to draw lines, not just arrows / floors do not switch on Streets",
  "feedback.note": "Your Discord display name and ID are sent along, so we can reply.",
  "feedback.send": "Send",
  "feedback.sending": "Sending…",
  "feedback.sent": "Sent. Thank you.",
  "feedback.failed": "Could not send",

  /* Time --------------------------------------------------------------- */
  "time.minutes": "{n} min",
  "time.hoursMinutes": "{h} h {m} min",

  /* Errors from the server --------------------------------------------- */
  "error.roomFull": "This room is full",
  "error.tooManyWindows": "Too many windows open on this room. Close one.",
  "error.serverFull": "The server is full",
  "error.session": "Your session is no longer valid. Please reopen.",
  "error.handoffSpent":
    "This link has already been opened. It is a one-time link for whoever made it. " +
    "To get into the same room, ask them for an invite link -- the map has an \"Invite someone\" button.",
  "error.handoffExpired":
    "This link has expired. They last 30 minutes. " +
    "Open \"Open in another window\" from the map again for a fresh one.",
  "error.handoffUnknown":
    "This link is not valid. Check that it was copied whole, " +
    "or open \"Open in another window\" from the map again.",
  "error.inviteSpent": "This invite link is no longer usable. Ask for a new one.",
  "error.inviteNotOwner": "Only the person who made this link can revoke it",
  "error.noRoom": "No room was given",
  "error.needName": "Please enter a name",
  "error.tooShort": "Please write a little more",
  "error.tooLong": "That is too long",
  "error.badKind": "Invalid category",
  "error.rateLimited": "Too many messages. Please wait about 10 minutes.",
  "error.busy": "Busy right now. Please try again shortly.",
  "error.sendFailed": "Could not send. Please try again later.",
  "error.feedbackClosed": "Feedback is closed at the moment",
  "error.sessionGone": "The original session has ended. Reopen from the Activity.",

  /* Landing -------------------------------------------------------------- */
  "landing.tagline1": "Share a Tarkov map",
  "landing.tagline2": "with everyone on a Discord call, and talk over it.",
  "landing.install": "Add to Discord",
  "landing.installNote": "Free. Launch it from the rocket icon in a voice channel.",
  "landing.browser": "Open in the browser",
  "landing.browserNote":
    "Signs in with Discord and opens a map right away. Share the address to bring people in -- they do not need to be on a call.",
  "landing.body":
    "Everyone on the call sees the same map. Click to drop a pin, and drag while placing it to add a direction arrow — so you can show not just where, but which way to go.",
  "landing.point1": "Being on the same call is enough. No codes to type",
  "landing.point2": "All 13 maps. Illustrated or satellite, and floors",
  "landing.point3": "Pins stay put, so you can look back at them later",
  "landing.point4": "People not on the call can join through an invite link",
  "landing.footNote":
    "It never touches the game. No files, no memory, nothing drawn over the game. Every pin is placed by hand.",
  "landing.footCredit":
    "A hobby project, run for free. Map data from {tarkovdev} (CC BY-NC-SA 4.0). Escape from Tarkov is a product of Battlestate Games Limited; this is an unofficial fan project with no connection to them.",
  "landing.updates": "Updates",
  "landing.allUpdates": "All updates",

  /* When it breaks --------------------------------------------------------- */
  "crash.title": "Something went wrong",
  "crash.body":
    "The map could not be drawn. The problem has been reported automatically. Reloading usually clears it.",
  "crash.code": "Reference",
  "crash.reload": "Reload",
  "trace.label": "ID",

  /* Boot ---------------------------------------------------------------- */
  "boot.connecting": "Connecting to Discord…",
  "boot.failedTitle": "Could not start",
  // Discord's own failures arrive as an object with no sentence in them.
  "boot.failedUnknown": "Discord did not answer. Please close the activity and open it again.",
} as const;

export type MessageKey = keyof typeof en;
export type Messages = Record<MessageKey, string>;
export type PartialMessages = Partial<Messages>;
